import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import cn from 'classnames';
import O_HeaderResponsive from '@/components/O_Organisms/O_Header/O_HeaderResponsive';
import O_ChatSidebar from '@/components/O_Organisms/O_ChatSidebar/O_ChatSidebar';
import O_ChatThread from '@/components/O_Organisms/O_ChatThread/O_ChatThread';
import S_NewChatPopup from '@/components/S_Super-Organisms/S_NewChatPopup/S_NewChatPopup';
import S_ChatMembersPopup from '@/components/S_Super-Organisms/S_ChatMembersPopup/S_ChatMembersPopup';
import { Conversation, chatApi } from '@/services/chatApi';
import { useConversations } from '@/hooks/useChat';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';
import { PATHS } from '@/constants/paths';
import styles from './ChatPage.module.css';

/**
 * Мессенджер: слева список диалогов, справа переписка.
 * На мобильных показывается одна панель — выбранный диалог перекрывает список.
 */
export default function ChatPage() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile(968);
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(language, key);

  const { user } = useAuth();
  const currentUserId = user?.id;

  const { conversations, isLoading, error, refresh, clearUnread, upsert } =
    useConversations();
  const [activeConversation, setActiveConversation] = useState<Conversation>();
  const [isNewChatOpen, setIsNewChatOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  // Детали (участники и роли) нужны отдельным запросом: в списке их нет.
  useEffect(() => {
    if (!conversationId) {
      setActiveConversation(undefined);
      return;
    }

    let isActive = true;
    const fallback = conversations.find(item => item.id === conversationId);
    setActiveConversation(previous =>
      previous?.id === conversationId ? previous : fallback
    );

    chatApi
      .getConversation(conversationId)
      .then(response => {
        if (isActive && response.data?.conversation) {
          setActiveConversation(response.data.conversation);
        }
      })
      .catch(() => {
        if (isActive && !fallback) {
          navigate(PATHS.CHAT, { replace: true });
        }
      });

    return () => {
      isActive = false;
    };
    // conversations намеренно не в зависимостях: перезагрузка списка
    // не должна повторно дёргать детали открытого диалога.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, navigate]);

  const openConversation = useCallback(
    (conversation: Conversation) => {
      setActiveConversation(conversation);
      clearUnread(conversation.id);
      navigate(PATHS.CHAT_CONVERSATION_BY_ID(conversation.id));
    },
    [clearUnread, navigate]
  );

  const handleConversationCreated = useCallback(
    (conversation: Conversation) => {
      setIsNewChatOpen(false);
      upsert(conversation);
      openConversation(conversation);
    },
    [openConversation, upsert]
  );

  const handleLeftConversation = useCallback(
    (leftConversationId: string) => {
      setIsDetailsOpen(false);
      setActiveConversation(undefined);
      void refresh();
      if (conversationId === leftConversationId) {
        navigate(PATHS.CHAT, { replace: true });
      }
    },
    [conversationId, navigate, refresh]
  );

  const showSidebar = !isMobile || !conversationId;
  const showThread = Boolean(conversationId) && Boolean(activeConversation);

  return (
    <div className={styles.wrapper}>
      <O_HeaderResponsive showSearchInput={!isMobile} />
      <main className={styles.content}>
        <div
          className={cn(styles.layout, { [styles.layout_mobile]: isMobile })}
        >
          {showSidebar && (
            <O_ChatSidebar
              conversations={conversations}
              activeConversationId={conversationId}
              currentUserId={currentUserId}
              isLoading={isLoading}
              error={error}
              onSelect={openConversation}
              onCreate={() => setIsNewChatOpen(true)}
            />
          )}

          {showThread && activeConversation ? (
            <O_ChatThread
              conversation={activeConversation}
              currentUserId={currentUserId}
              onBack={isMobile ? () => navigate(PATHS.CHAT) : undefined}
              onOpenDetails={() => setIsDetailsOpen(true)}
              onRead={clearUnread}
              onMessageSent={refresh}
            />
          ) : (
            !isMobile && (
              <section className={styles.placeholder}>
                <p className={cn(styles.placeholder_text, 'text-p-7')}>
                  {t('chat.page.selectConversation')}
                </p>
              </section>
            )
          )}
        </div>
      </main>

      {isNewChatOpen && (
        <S_NewChatPopup
          onClose={() => setIsNewChatOpen(false)}
          onConversationReady={handleConversationCreated}
        />
      )}

      {isDetailsOpen && activeConversation && (
        <S_ChatMembersPopup
          conversation={activeConversation}
          currentUserId={currentUserId}
          onClose={() => setIsDetailsOpen(false)}
          onUpdated={conversation => {
            setActiveConversation(conversation);
            upsert(conversation);
          }}
          onLeft={handleLeftConversation}
        />
      )}
    </div>
  );
}
